"use client"

import styled from "@emotion/styled"
import { Skeleton } from "@mui/material"
import { useLocale } from "next-intl"
import Script from "next/script"
import React from "react"
import { sharedColors } from "ui/theme/colors"
import { FormObject } from "./Hero"
import { defaultColors } from "./hopOnConfig"

// Declare global window interface for HopOn widget
declare global {
  interface Window {
    renderBnbWidget?: (containerId: string, options: HopOnWidgetOptions) => void
  }
}

interface HopOnWidgetOptions {
  language: string
  fontFamily: string
  colors: typeof defaultColors
  onSearch: (url: string) => void
}

const HOPON_SCRIPT_URL = "https://bnb-widget.hopon.co/bnb-widget.iife.js"

const desktopHeight = 532
const mobileHeight = 532

const StyledWidgetWrapper = styled.div`
  height: ${mobileHeight}px;
  overflow: hidden;
  border-radius: 8px;

  ${({ theme }) => theme.breakpoints.up("md")} {
    height: ${desktopHeight}px;
  }
`

const StyledWidgetContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 320px;
`

// Widget locale titles for accessibility
const hopOnLocaleConfig = {
  he: {
    title: "חיפוש נסיעה בתחבורה ציבורית",
  },
  en: {
    title: "Search public transportation routes",
  },
  ar: {
    title: "البحث عن طرق النقل العام",
  },
  ru: {
    title: "Поиск маршрутов общественного транспорта",
  },
}

type Props = {
  formObject: FormObject
}

const HopOnWidget: React.FC<Props> = () => {
  const locale = useLocale()
  const [loaded, setLoaded] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [isMounted, setIsMounted] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const widgetRendered = React.useRef(false)
  const currentLocale = React.useRef(locale)

  const widgetTitle =
    hopOnLocaleConfig[locale as keyof typeof hopOnLocaleConfig]?.title ||
    hopOnLocaleConfig.he.title
  const containerId = React.useMemo(
    () => `hopon-widget-container-${locale}`,
    [locale],
  )

  // Reset everything when locale changes
  React.useEffect(() => {
    if (currentLocale.current !== locale) {
      setLoaded(false)
      setError(null)
      widgetRendered.current = false
      currentLocale.current = locale
    }
  }, [locale])

  // Mount state only on client side to avoid hydration mismatch
  React.useEffect(() => {
    setIsMounted(true)
  }, [])

  const handleSearch = React.useCallback(
    (url: string) => {
      // Extract search parameters from HopOn URL and redirect to our journey planner
      if (typeof window !== "undefined") {
        try {
          const hopOnUrl = new URL(url)
          const searchParams = new URLSearchParams()
          const path = hopOnUrl.pathname
          // Copy all parameters from HopOn URL to our journey planner
          hopOnUrl.searchParams.forEach((value, key) => {
            searchParams.append(key, value)
          })
          if (path && path !== "/") {
            searchParams.append("path", path)
          }

          // Build our journey planner URL with the search parameters
          const journeyPlannerUrl = `/${locale}/journey-planner?${searchParams.toString()}`

          // Navigate to our journey planner page
          window.location.href = journeyPlannerUrl
        } catch (err) {
          console.error("Failed to parse HopOn URL:", err)
          // Fallback to opening in new tab
          window.open(url, "_blank")
        }
      }
    },
    [locale],
  )

  const getHopOnConfig = React.useCallback((): HopOnWidgetOptions => {
    return {
      language: locale,
      fontFamily: locale === "he" ? "Noto Sans Hebrew" : "Open Sans",
      colors: defaultColors,
      onSearch: handleSearch,
    }
  }, [locale, handleSearch])

  const renderWidget = React.useCallback(() => {
    if (!loaded || !containerRef.current || widgetRendered.current) return

    try {
      // Clear the container and render the widget
      containerRef.current.innerHTML = ""
      const config = getHopOnConfig()
      ;(window as any).renderBnbWidget(containerId, config)
      widgetRendered.current = true
      setError(null)
    } catch (err) {
      console.error("Failed to render HopOn widget:", err)
      setError(err instanceof Error ? err.message : "Failed to load widget")
    }
  }, [loaded, containerId, getHopOnConfig])

  React.useEffect(() => {
    if (loaded && isMounted) {
      renderWidget()
    }
  }, [loaded, renderWidget, isMounted])

  // Check if script is already loaded when component mounts or locale changes
  React.useEffect(() => {
    if (
      isMounted &&
      typeof window !== "undefined" &&
      (window as any).renderBnbWidget &&
      !loaded
    ) {
      setLoaded(true)
    }
  }, [isMounted, loaded])

  const handleScriptLoad = React.useCallback(() => {
    setLoaded(true)
  }, [])

  const handleScriptError = React.useCallback((error: any) => {
    console.error("Failed to load HopOn script:", error)
    setError("Failed to load widget script")
  }, [])

  // Don't render anything until mounted to avoid hydration issues
  if (!isMounted) {
    return (
      <StyledWidgetWrapper>
        <Skeleton
          variant="rectangular"
          height={55}
          width="100%"
          sx={{
            bgcolor: sharedColors.brandMain,
            opacity: 0.5,
            marginBottom: "8px",
          }}
        />
        <Skeleton
          variant="rectangular"
          height={55}
          width="100%"
          sx={{
            bgcolor: sharedColors.brandMain,
            opacity: 0.5,
            marginBottom: "8px",
          }}
        />
        <Skeleton
          variant="rectangular"
          height={55}
          width="100%"
          sx={{
            bgcolor: sharedColors.brandMain,
            opacity: 0.5,
            marginBottom: "8px",
          }}
        />
      </StyledWidgetWrapper>
    )
  }

  const showFallback = !loaded || !!error

  return (
    <StyledWidgetWrapper>
      <Script
        src={HOPON_SCRIPT_URL}
        strategy="afterInteractive"
        onLoad={handleScriptLoad}
        onError={handleScriptError}
      />
      <StyledWidgetContainer
        ref={containerRef}
        id={containerId}
        role="search"
        aria-label={widgetTitle}
        suppressHydrationWarning={true}
      >
        {showFallback && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div
              style={{
                height: "55px",
                backgroundColor: sharedColors.brandMain,
                opacity: 0.5,
                borderRadius: "4px",
              }}
            />
            <div
              style={{
                height: "55px",
                backgroundColor: sharedColors.brandMain,
                opacity: 0.5,
                borderRadius: "4px",
              }}
            />
            <div
              style={{
                height: "55px",
                backgroundColor: sharedColors.brandMain,
                opacity: 0.5,
                borderRadius: "4px",
              }}
            />
          </div>
        )}
      </StyledWidgetContainer>
    </StyledWidgetWrapper>
  )
}

export default HopOnWidget
